import { DocumentTextIcon } from "@heroicons/vue/24/outline";
import ImageIcon from "@/components/icons/ImageIcon.vue";
import VideoIcon from "@/components/icons/VideoIcon.vue";
import CsvIcon from "@/components/icons/CsvIcon.vue";
import PowerpointIcon from "@/components/icons/PowerpointIcon.vue";
import type { FileCategory, IFileItem, SupportedFileExtension } from "@/types/files";

export const fileCategoryArr: FileCategory[] = [
  "mediaAssets",
  "brandAndStrategy",
  "productInformation",
  "competitorResearch",
  "campaigns",
];

export const blobToFile = (theBlob: Blob, fileName: string, fileType?: string): File => {
  const b: any = theBlob;
  b.lastModifiedDate = new Date();
  b.name = fileName;

  return new File([b], fileName, {
    type: fileType,
  });
};

export function getFilePreviewUrl(file: File) {
  if (typeof URL === "function") {
    return URL.createObjectURL(file as Blob) as any;
  } else if (FileReader) {
    return new FileReader().readAsDataURL(file);
  }
  return "";
}

export function getFileIcon(file?: IFileItem) {
  if (!file) return DocumentTextIcon;
  if (file.contentType.includes("image/")) return ImageIcon;
  if (file.contentType.startsWith("video/")) return VideoIcon;
  const type = getFileType(file.contentType);

  switch (type) {
    case "png":
    case "jpg":
      return ImageIcon;
    case "pdf":
      return "pi-file-pdf";
    case "csv":
      return CsvIcon;
    case "xlsx":
      return "pi-file-excel";
    case "docx":
      return "pi-file-word";
    case "ppt":
      return PowerpointIcon;
    case "txt":
      return "pi-file";
    default:
      return DocumentTextIcon;
  }
}

export const supportedFileExtensions: SupportedFileExtension[] = [
  "csv",
  "xlsx",
  "xls",
  "pdf",
  "docx",
  "doc",
  "pptx",
  "ppt",
  "txt",
  "jpg",
  "jpeg",
  "png",
  "webp",
  "mp4",
  "webm",
  "mov",
];

export function getFileType(
  contentType: string
): Exclude<SupportedFileExtension, "jpeg" | "xls" | "pptx" | "webp" | "doc"> | "" {
  if (!contentType) return "";

  switch (contentType) {
    case "application/pdf":
      return "pdf";
    case "image/png":
    case "image/x-png":
      return "png";
    case "image/jpeg":
    case "image/jpg":
    case "image/webp":
      return "jpg";
    case "text/csv":
    case "application/csv":
      return "csv";
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/msexcel":
    case "application/x-msexcel":
    case "application/x-excel":
    case "application/x-dos_ms_excel":
    case "application/xls":
      return "xlsx";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-powerpoint":
    case "application/mspowerpoint":
    case "application/powerpoint":
    case "application/x-mspowerpoint":
      return "ppt";
    case "text/plain":
    case "application/txt":
      return "txt";
    case "video/mp4":
      return "mp4";
    case "video/webm":
      return "webm";
    case "video/quicktime":
      return "mov";
    default:
      return "";
  }
}

export function getMimeTypesByExtension(extension: SupportedFileExtension) {
  switch (extension) {
    case "csv":
      return ["text/csv", "application/csv", "application/vnd.ms-excel"];
    case "xlsx":
    case "xls":
      return [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/msexcel",
        "application/x-msexcel",
        "application/x-excel",
        "application/x-dos_ms_excel",
        "application/xls",
      ];
    case "pdf":
      return ["application/pdf"];
    case "docx":
    case "doc":
      return [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "application/doc",
        "application/vnd.ms-word",
        "application/x-msword",
      ];
    case "pptx":
    case "ppt":
      return [
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-powerpoint",
        "application/mspowerpoint",
        "application/powerpoint",
        "application/x-mspowerpoint",
      ];
    case "txt":
      return ["text/plain", "application/txt"];
    case "jpg":
    case "jpeg":
      return ["image/jpeg", "image/jpg"];
    case "png":
      return ["image/png", "image/x-png"];
    case "webp":
      return ["image/webp"];
    case "ico":
      return ["image/vnd.microsoft.icon", "image/x-icon"];
    case "svg":
      return ["image/svg+xml"];
    case "mp4":
      return ["video/mp4"];
    case "webm":
      return ["video/webm"];
    case "mov":
      return ["video/quicktime"];
    default:
      return [];
  }
}

export function getFileMimeTypesForExtensions(extensions: SupportedFileExtension[]) {
  if (!extensions || !Array.isArray(extensions) || !extensions.length) return [];

  return extensions.flatMap(extension => getMimeTypesByExtension(extension));
}

export function getAllSupportedMimeTypes() {
  return supportedFileExtensions.flatMap(extension => getMimeTypesByExtension(extension));
}
